import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth";
import type { UnifiedOrderRequest, UnifiedOrderResponse } from "@/api.types.v2";
import { logger } from "@/services/logger";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Create a unified order with shipment and packages
 * Uses the canonical /api/v2/orders endpoint
 */
export function useCreateUnifiedOrder(options?: {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}) {
  const queryClient = useQueryClient();
  const { token, customer } = useSession();

  return useMutation({
    mutationFn: async (request: UnifiedOrderRequest) => {
      // Ensure customer ID is set
      const payload = {
        ...request,
        placerCustomerId: customer?.id,
      };

      logger.info("Creating unified order", {
        shipmentCount: payload.shipments.length,
        packageCount: payload.shipments[0]?.packages.length,
      });

      const { data } = await axios.post<UnifiedOrderResponse>(
        `${API_URL}/api/v2/orders`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return data;
    },
    onSuccess: async () => {
      // Invalidate orders list to refetch (both v1 and v2 queries)
      await queryClient.invalidateQueries({
        queryKey: ["customerOrders", customer?.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["customerOrdersV2", customer?.id],
      });

      logger.info("Order created successfully");
      options?.onSuccess?.();
    },
    onError: (error) => {
      logger.error("Error creating unified order:", error);
      options?.onError?.(error);
    },
  });
}
